import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { devConfig } from '../../config/devConfig';
import { createTestAccount } from '../../../api/localAuth';
import { clearLocalSessionToken } from '../../../api/authToken';
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  MenuItem,
  FormControlLabel,
  Switch,
  Card,
  CardContent
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const IS_LOCAL_DEV = process.env.NODE_ENV === 'development';

const packageOptions = [
  { id: 'free_trial', name: 'Free Trial' },
  { id: 'business', name: 'Business' },
  { id: 'business_plus', name: 'Business Plus' },
  { id: 'enterprise', name: 'Enterprise' }
];

function sampleValues() {
  const stamp = Date.now().toString(36);
  return {
    companyName: `Test Company ${stamp}`,
    firstName: 'Test',
    lastName: 'Owner',
    email: `test.owner.${stamp}@example.com`,
    password: 'TestPass123!',
  };
}

const TestAccountsPage = () => {
  const navigate = useNavigate();
  const [accessCodeInput, setAccessCodeInput] = useState('');
  const [isAuthorized, setIsAuthorized] = useState(IS_LOCAL_DEV);
  const [authError, setAuthError] = useState('');

  const [companyName, setCompanyName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState('enterprise');
  const [isUnlimited, setIsUnlimited] = useState(true);

  const handleAuthorize = (e) => {
    e.preventDefault();
    setAuthError('');
    if (!accessCodeInput) {
      setAuthError('Access code is required.');
      return;
    }
    if (accessCodeInput !== devConfig.TEST_ACCOUNTS_ACCESS_CODE) {
      setAuthError('Invalid access code.');
      return;
    }
    setIsAuthorized(true);
  };

  const fillSample = () => {
    const sample = sampleValues();
    setCompanyName(sample.companyName);
    setFirstName(sample.firstName);
    setLastName(sample.lastName);
    setEmail(sample.email);
    setPassword(sample.password);
    setConfirmPassword(sample.password);
    setError('');
    setSuccess('');
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!companyName || !firstName || !lastName || !email || !password || !confirmPassword) {
      setError('All fields are required.');
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }
    try {
      setLoading(true);
      await createTestAccount({
        companyName,
        firstName,
        lastName,
        email,
        password,
        packageId: selectedPackage,
        unlimited: isUnlimited,
      });
      clearLocalSessionToken();
      setSuccess(`Created ${email}. You can log in now.`);
      navigate('/app/login', {
        state: {
          message: `Test account created. Log in with ${email}.`
        }
      });
    } catch (err) {
      setError(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthorized) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Card sx={{ width: '100%', maxWidth: 420 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, textAlign: 'center' }}>
              Developer Access
            </Typography>
            {authError && <Alert severity="error" sx={{ mb: 2 }}>{authError}</Alert>}
            <form onSubmit={handleAuthorize}>
              <TextField
                fullWidth
                label="Access Code"
                value={accessCodeInput}
                onChange={(e) => setAccessCodeInput(e.target.value)}
                type="password"
              />
              <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }}>
                Enter
              </Button>
            </form>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Box sx={{ width: '100%', maxWidth: 640, p: 3, borderRadius: 3, boxShadow: 2, bgcolor: 'background.paper' }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, textAlign: 'center' }}>
          Test Accounts
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', textAlign: 'center' }}>
          Create a main-app company owner in Postgres. No payment, no email verification.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>
        )}
        <form onSubmit={handleRegister}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Button type="button" variant="outlined" fullWidth onClick={fillSample}>
                Fill sample account
              </Button>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Package"
                value={selectedPackage}
                onChange={(e) => setSelectedPackage(e.target.value)}
              >
                {packageOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Company Name"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessIcon color="primary" />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="primary" />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="primary" />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="primary" />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="primary" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((v) => !v)} edge="end" size="small">
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="primary" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword((v) => !v)} edge="end" size="small">
                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <FormControlLabel
              control={<Switch checked={isUnlimited} onChange={(e) => setIsUnlimited(e.target.checked)} />}
              label="Unlimited test subscription"
            />
          </Box>
          <Button type="submit" variant="contained" fullWidth sx={{ mt: 3 }} disabled={loading}>
            {loading ? <CircularProgress size={22} color="inherit" /> : 'Create Test Account'}
          </Button>
        </form>
      </Box>
    </Box>
  );
};

export default TestAccountsPage;
